import { create } from "zustand";
import { persist, type PersistStorage } from "zustand/middleware";
import {
  nextThemePref,
  resolveTheme,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type ThemePref,
  type ThemeScopeVariant,
} from "@/lib/theme";

/**
 * The single source of truth for the theme preference.
 *
 * There used to be three independent copies of this state: `ThemeToggle` held
 * its own `useState` seeded from localStorage once on mount, `ThemeScope` had a
 * verbatim duplicate of the storage reader, and `AdminCommandPalette` wrote the
 * SAME localStorage key + stamped `<html data-theme>` directly, notifying
 * nobody. In admin both the toggle and the palette are mounted at once, so
 * ⌘K → "Toggle theme" flipped the page while the toggle's label/aria-label went
 * stale — and because its cycle computed `nextThemePref(prev)` from that stale
 * value, the NEXT click jumped to the wrong theme in the three-way cycle.
 *
 * Everything now reads and writes this store.
 *
 * ⚠ THE PERSIST KEY IS A CONTRACT. `THEME_INIT_SCRIPT` (lib/theme.ts) runs
 * inline in <head> BEFORE first paint and does a bare
 * `localStorage.getItem("theme")`, comparing against the bare strings
 * "light"/"dark"/"system". Zustand's default serializer would write
 * `{"state":{…},"version":0}`, which that script does not recognise — every
 * dark load would flash light. Hence {@link barePrefStorage} below: a custom
 * `PersistStorage` that reads/writes the BARE STRING. Guarded by
 * THEMESYNC-1 in tests/unit/stores/theme-store.spec.ts.
 *
 * ⚠ THEME IS ROUTE-SCOPED. The stored preference is a STAFF setting, honoured
 * ONLY under /admin and /florist; every customer-facing route is forced to
 * light regardless of what is stored (see the doc-block in lib/theme.ts) — a
 * staff member who picks Dark must never see the storefront go dark.
 *
 * ⚠ THE SCOPE IS NOT IN THIS STORE, ON PURPOSE. It used to be, published by a
 * `setScope(variant)` call from a MOUNT EFFECT in each of the four `ThemeScope`
 * mounts. A cross-group client-side navigation (storefront ↔ staff) therefore
 * rendered — and could paint — one frame at the PREVIOUS group's theme before
 * that passive effect landed: the same first-render failure that got
 * `stores/sidebar-store.ts` deleted. The scope is a pure function of the URL, so
 * it is derived from `usePathname()` at the point of use
 * ({@link themeScopeForPath}) and passed into {@link resolvedThemeFor}. This
 * store holds only what genuinely has no other owner: the persisted preference
 * and the live OS setting. Stamping `<html data-theme>` belongs to
 * `components/theme/ThemeScope.tsx`, which is the only place that knows the
 * route.
 */

/** The inputs the applied theme is derived from (never store the result). */
export interface ThemeInputs {
  /** The persisted staff preference. */
  pref: ThemePref;
  /** Live `prefers-color-scheme: dark`, tracked while `pref` is "system". */
  systemDark: boolean;
  /**
   * The route group in view. "storefront" forces light. A PARAMETER, never a
   * stored field — derive it with `themeScopeForPath(usePathname())`.
   */
  scope: ThemeScopeVariant;
}

/** What the store actually owns: `ThemeInputs` minus the route-derived scope. */
type ThemeOwnedInputs = Omit<ThemeInputs, "scope">;

interface ThemeState extends ThemeOwnedInputs {
  setPref: (pref: ThemePref) => void;
  /** Advance the System → Light → Dark → System cycle from the LIVE value. */
  cyclePref: () => void;
  setSystemDark: (systemDark: boolean) => void;
}

/**
 * Factory, not a shared literal — the store is a module singleton and a Workers
 * isolate is reused across requests, so a shared mutable default could leak.
 * Defaults mirror THEME_INIT_SCRIPT: light, OS setting unknown.
 */
const initialState = (): ThemeOwnedInputs => ({
  pref: "light",
  systemDark: false,
});

/**
 * The applied theme. Derived, never stored: `storefront` is light-only, and
 * only under a staff scope does the stored preference resolve.
 */
export function resolvedThemeFor(inputs: ThemeInputs): ResolvedTheme {
  if (inputs.scope === "storefront") return "light";
  return resolveTheme(inputs.pref, inputs.systemDark);
}

/** Current OS setting, safe on the server and where matchMedia is missing. */
export function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Only the preference is persisted; the OS setting is a per-page fact. */
type ThemePersisted = { pref: ThemePref };

function isThemePref(value: unknown): value is ThemePref {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * Bare-string localStorage adapter. Reads/writes `"light"|"dark"|"system"` at
 * `THEME_STORAGE_KEY` so the pre-paint script keeps recognising its own value.
 * Returns no `version`, which makes zustand skip its version/migrate branch
 * entirely. Every access is wrapped so a blocked localStorage (private mode,
 * cookie-less embed) degrades to the default instead of throwing.
 */
const barePrefStorage: PersistStorage<ThemePersisted> = {
  getItem: (name) => {
    try {
      const raw = globalThis.localStorage?.getItem(name);
      if (isThemePref(raw)) return { state: { pref: raw } };
    } catch {
      /* localStorage blocked — fall through to the light default */
    }
    return null;
  },
  setItem: (name, value) => {
    try {
      globalThis.localStorage?.setItem(name, value.state.pref);
    } catch {
      /* localStorage blocked — the preference just doesn't survive the tab */
    }
  },
  removeItem: (name) => {
    try {
      globalThis.localStorage?.removeItem(name);
    } catch {
      /* localStorage blocked — nothing to remove */
    }
  },
};

export const useThemeStore = create<ThemeState>()(
  persist(
    // Pure state. `<html data-theme>` is stamped by `ThemeScope`, which reacts to
    // these values AND to the route — the store cannot resolve a theme on its
    // own, because it deliberately does not know the scope.
    (set, get) => ({
      ...initialState(),

      setPref: (pref) => set({ pref }),

      cyclePref: () => set({ pref: nextThemePref(get().pref) }),

      setSystemDark: (systemDark) => set({ systemDark }),
    }),
    {
      name: THEME_STORAGE_KEY,
      storage: barePrefStorage,
      partialize: (state): ThemePersisted => ({ pref: state.pref }),
    },
  ),
);
